import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  DeleteDialogData
} from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { FetchOptionsStateModel } from '../../core/state/fetchOptions.state';
import { catchError, filter, of, switchMap, tap } from 'rxjs';
import { ConsumerJsonModel } from '../consumer.json.model';
import { ConsumerService } from '../consumer.service';
import { ConsumerActions } from './consumer.actions';
import { ConsumerEditComponent } from '../edit-consumer/edit-consumer.component';

export interface ConsumerStateModel {
  consumers: ConsumerJsonModel[]
}

@State<ConsumerStateModel>({
  name: 'consumer',
  defaults: {
    consumers: []
  }
})
@Injectable()
export class ConsumerState {
  private readonly consumerService = inject(ConsumerService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  @Selector()
  static getState(state: ConsumerStateModel) {
    return state;
  }

  @Selector()
  static getConsumers(state: ConsumerStateModel) {
    return state.consumers;
  }

  @Selector()
  static getAmountOfConsumers(state: ConsumerStateModel) {
    return state.consumers?.length ?? 0;
  }

  @Selector()
  static consumersIsEmpty(state: ConsumerStateModel) {
    return (state.consumers?.length ?? 0) <= 0;
  }

  @Selector()
  static getSortedConsumers(state: ConsumerStateModel): ConsumerJsonModel[] {
    return [...state.consumers].sort((a, b) => {
      return a.name.localeCompare(b.name, 'de', { sensitivity: 'base', numeric: true });
    });
  }

  @Action(ConsumerActions.Fetch)
  onFetchConsumers(ctx: StateContext<ConsumerStateModel>) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);
    return this.consumerService.getAllWithPagination({}, localFetchOptions).pipe(
      tap(res => {
        ctx.dispatch(new ConsumerActions.FetchSuccess(res[0]));
      })
    );
  }

  @Action(ConsumerActions.FetchAll)
  onFetchAllConsumers(ctx: StateContext<ConsumerStateModel>) {
    return this.consumerService.getMany({}).pipe(
      tap(res => ctx.dispatch(new ConsumerActions.FetchSuccess(res.data)))
    );
  }

  @Action(ConsumerActions.FetchSuccess)
  onFetchConsumersSuccess(ctx: StateContext<ConsumerStateModel>, { payload }: ConsumerActions.FetchSuccess) {
    ctx.setState({ consumers: payload });
  }

  @Action(ConsumerActions.Add)
  onAdd(ctx: StateContext<ConsumerStateModel>, { payload }: ConsumerActions.Add) {
    return this.dialog.open<ConsumerEditComponent, ConsumerJsonModel, ConsumerJsonModel>(ConsumerEditComponent, { data: payload })
      .afterClosed()
      .pipe(
        filter((consumer): consumer is ConsumerJsonModel => !!consumer),
        switchMap(consumer => this.consumerService.createAndSave(consumer)),
        // currently ignores errors
        catchError(() => of()),
        tap(() => ctx.dispatch(new ConsumerActions.Fetch()))
      );
  }

  @Action(ConsumerActions.Edit)
  onEdit(ctx: StateContext<ConsumerStateModel>, { payload }: ConsumerActions.Edit) {
    return this.dialog.open<ConsumerEditComponent, ConsumerJsonModel, ConsumerJsonModel>(ConsumerEditComponent,
      {
        data: payload,
        width: '80vw',
        maxHeight: '90vh',
        maxWidth: '80vw'
      }
    )
      .afterClosed()
      .pipe(
        filter((consumer): consumer is ConsumerJsonModel => !!consumer),
        switchMap(consumer => this.consumerService.update(consumer)),
        catchError(() => of()),
        tap(() => ctx.dispatch(new ConsumerActions.Fetch()))
      );
  }

  @Action(ConsumerActions.FetchAppend)
  onFetchAppendConsumers(ctx: StateContext<ConsumerStateModel>) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);
    return this.consumerService.getAllWithPagination({}, localFetchOptions).pipe(
      tap(res => {
        ctx.dispatch(new ConsumerActions.FetchSuccessAppend(res[0]));
      })
    );
  }

  @Action(ConsumerActions.FetchSuccessAppend)
  onFetchConsumersSuccessAppend(ctx: StateContext<ConsumerStateModel>, { payload }: ConsumerActions.FetchSuccess) {
    const state = ctx.getState();
    ctx.setState({
      consumers: [...state.consumers, ...payload]
    });
  }

  @Action(ConsumerActions.Delete)
  onDelete(ctx: StateContext<ConsumerJsonModel>, { payload }: ConsumerActions.Delete) {
    return this.dialog.open<ConfirmDialogComponent, DeleteDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        id: payload.id,
        name: payload.name,
        itemType: 'Konsument',
        deleteFn: () => {
          return payload.destroy();
        }
      }
    })
      .afterClosed()
      .pipe(
        tap(success => {
          if (!success) return;
          ctx.dispatch(new ConsumerActions.Fetch());
        })
      );
  }
}
